/**
 * WHAT A SUBMIT DOES — the composer's decision, out of App.tsx (M5, 2026-09-03).
 *
 * One press of Send answers three questions at once: does anything go at all, does it
 * go as FILES or as a MESSAGE, and what happens to the typed text. App answered them
 * inline, and the census's complaint was the usual one — the decision was reachable
 * only through the mounted composer.
 *
 * Extracting it found a bug, which is this campaign's steadiest pattern. The inline
 * check was `hasStaged = staged.length > 0` — ERRORED items counted — and the files
 * branch then filtered them out and uploaded the remainder. Stage one oversize file,
 * type a caption, press Send: the branch is taken, ZERO files upload, and the branch's
 * epilogue clears the composer. The typed text is destroyed, nothing was sent, nothing
 * says so. The plan below counts only SENDABLE files when choosing the branch, so that
 * press sends the text as an ordinary message — the errored chip stays on screen,
 * still wearing its own error.
 *
 * The caption rule rides along: text typed beside staged files becomes the caption of
 * the FIRST sendable file (Slack's shape — one card, caption above, file below), and
 * only the first, or a three-photo drop repeats its caption three times.
 */

export type Staged = { id: string; err?: string | undefined }

export type SendPlan =
  | { kind: 'nothing' }
  /** Upload these staged ids in order; `captionOn` names the one that carries the
   *  typed text (always the first sendable, null only when there is none). */
  | { kind: 'files'; ids: string[]; captionOn: string | null }
  | { kind: 'message' }

export function planSend(args: { body: string; staged: Staged[]; inProject: boolean }): SendPlan {
  if (!args.inProject) return { kind: 'nothing' }
  const sendable = args.staged.filter((s) => !s.err).map((s) => s.id)
  if (sendable.length > 0) {
    return { kind: 'files', ids: sendable, captionOn: sendable[0] ?? null }
  }
  if (args.body) return { kind: 'message' }
  return { kind: 'nothing' }
}
